import { cloneDeep } from 'lodash';
import { scaleLayers, trackConfig } from '@/framework';
import {
    IExportPlugin,
    ILayerProps,
    IMarkupLayerProps,
    IMarkupPoint,
    ISize,
    LayerType,
    MarkupDasharrayType,
    MarkupLabelType,
    MarkupLabelUnit,
    MarkupLinecapType,
    MarkupTextPositionType,
    MaterialIdsType,
    PluginType,
} from '@/version-types';
import { Layer, layerPlugin } from '../layer';
import markupLayerBehavior from './behavior';
import {
    clampFontSize,
    fixDrawResult,
    fixLabelSize,
    setFillColor,
    setFillStyle,
    setFillWidth,
    setLabelBackground,
    setLabelColor,
    setLabelFontFamily,
    setLabelFontSize,
    setLabelPosition,
    setLabelRotate,
    setLabelStyle,
    setLabelType,
    setLine,
    setPoint,
    setSwap,
    updateScreenshotUrl,
} from './utils';

export * from './utils';
export { default as MarkupLayerView } from './views/markup-layer';

// 初始化，字体丢失，修正图层宽高
export function fixMarkupLayerSize(layer: ILayerProps) {
    fixLabelSize(layer);
    fixDrawResult(layer, cloneDeep(layer));
}

/** 标注图层 */
export class MarkupLayer extends Layer {
    /** 用于属性变更计算的原始图层数据 */
    private operateOriginLayer?: ILayerProps | null = null;

    constructor(identity: string) {
        super(identity);
    }

    /** 修正配方的图层宽高 */
    fixLayerSize(layer: ILayerProps) {
        fixMarkupLayerSize(layer);
    }

    /** 设置操作的原始数据 */
    setAttrOperateOriginData(layer: ILayerProps) {
        this.operateOriginLayer = layer;
    }

    /** 清除操作的原始数据 */
    clearAttrOperateOriginData() {
        this.operateOriginLayer = null;
    }

    /** 字内容 */
    setLabelType(
        id: string,
        labelType: MarkupLabelType,
        labelText: string,
        labelNum: number,
        labelUnits: MarkupLabelUnit[],
        oldLayer?: ILayerProps,
        isResetActiveLayer = true,
    ) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        setLabelType(layer, labelType, labelText, labelNum, labelUnits, this.operateOriginLayer);
        this.framework.stage.addOrUpdate(layer);
        if (isResetActiveLayer) {
            this.framework.operate.setActiveLayer(layer.id);
        }
        if (oldLayer) {
            this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelType);
            updateScreenshotUrl(this.identity, layer.id);
        }
    }

    /** 字位子 */
    setLabelPosition(id: string, labelPosition: MarkupTextPositionType) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        setLabelPosition(layer, labelPosition, this.operateOriginLayer);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelPosition);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 字角度 */
    setLabelRotate(id: string, labelRotate: number, oldLayer?: ILayerProps) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        setLabelRotate(layer, labelRotate, this.operateOriginLayer);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        if (oldLayer) {
            this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelRotate);
            updateScreenshotUrl(this.identity, layer.id);
        }
    }

    /** 字体 */
    async setLabelFontFamily(id: string, fontFamily: string) {
        const materialId = await this.framework.registerFont(this.framework.getLayer(id), fontFamily);
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        const materialIds = layer.materialIds || {};
        materialIds[MaterialIdsType.FONT_FAMILY] = [`${materialId}`];
        setLabelFontFamily(layer, fontFamily, materialIds, this.operateOriginLayer);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelFontFamily);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 字号 */
    setLabelFontSize(id: string, fontSize: number) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        setLabelFontSize(layer, fontSize, this.operateOriginLayer);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelFontSize);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 字颜色 */
    setLabelColor(id: string, labelColor: string, oldLayer?: ILayerProps) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        setLabelColor(layer, labelColor);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        if (oldLayer) {
            this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelColor);
            updateScreenshotUrl(this.identity, layer.id);
        }
    }

    /** 字样式 */
    setLabelStyle(id: string, bold: boolean, italic: boolean) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        setLabelStyle(layer, bold, italic, this.operateOriginLayer);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelStyle);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 字背景色 */
    setLabelBackground(id: string, background: string, oldLayer?: ILayerProps) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        setLabelBackground(layer, background, this.operateOriginLayer);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        if (oldLayer) {
            this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].labelBackground);
            updateScreenshotUrl(this.identity, layer.id);
        }
    }

    /** 线颜色 */
    setFillColor(id: string, fillColor: string, oldLayer?: ILayerProps) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        setFillColor(layer, fillColor);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        if (oldLayer) {
            this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].fillColor);
            updateScreenshotUrl(this.identity, layer.id);
        }
    }

    /** 线样式 */
    setFillStyle(id: string, dasharray: MarkupDasharrayType, linecap: MarkupLinecapType) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        setFillStyle(layer, dasharray, linecap);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].fillStyle);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 线粗细 */
    setFillWidth(id: string, width: number, oldLayer?: ILayerProps) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        setFillWidth(layer, width, this.operateOriginLayer);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        if (oldLayer) {
            this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].fillWidth);
            updateScreenshotUrl(this.identity, layer.id);
        }
    }

    /** 端点 */
    setPoint(id: string, point: IMarkupPoint, isStartPoint: boolean) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        setPoint(layer, point, isStartPoint, this.operateOriginLayer);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].point);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 交换端点 */
    async swapPoint(id: string) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        const data = layer.data as IMarkupLayerProps;
        setSwap(layer, cloneDeep(data.endPoint), cloneDeep(data.startPoint));
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].swap);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 延长线 */
    async setLine(id: string, hasLine: boolean) {
        const layer = this.framework.getLayer(id, true);
        if (!layer) return;
        const oldLayer = cloneDeep(layer);
        setLine(layer, hasLine);
        this.framework.stage.addOrUpdate(layer);
        this.framework.operate.setActiveLayer(layer.id);
        this.framework.history.write(oldLayer, trackConfig[LayerType.MARKUP_LAYER].line);
        updateScreenshotUrl(this.identity, layer.id);
    }

    /** 添加预设标注 */
    async addPresetMarkup(layer: ILayerProps, options: { originSize: ISize; destSize: ISize; trackFunc?: string }) {
        const { destSize, originSize, trackFunc } = options;

        //矫正字体大小
        const ratio = destSize.width / 800;
        const data = layer.data as IMarkupLayerProps;
        data.label.fontSize = clampFontSize(data.label.fontSize * ratio);

        if (originSize && destSize) {
            scaleLayers(this.identity, [layer], originSize, destSize, false, false, true);
        }
        fixMarkupLayerSize(layer);

        this.addByDiagonal({ layer, trackFunc });
    }
}

/**
 * 标注笔图层插件
 */
export const markupLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.MARKUP_LAYER,
    createInstance({ identity }) {
        return [{ key: 'MarkupLayer', value: new MarkupLayer(identity) }];
    },
    createBehavior() {
        return markupLayerBehavior;
    },
};
